import { doLinesIntersect, Point } from './doLinesIntersect';

const heatMapZoneRegex = /^(.*?):(\d+),(\d+),(\d+),(\d+),(\d+),(\d+),(\d+),(\d+)$/;

export type PointIndex = 0 | 1 | 2 | 3;

export class HeatMapZone {
    readonly name: string;
    readonly points: ReadonlyArray<Readonly<Point>>;

    static fromString(input: string): HeatMapZone {
        const match = heatMapZoneRegex.exec(input);
        if (!match || match.length !== 10) {
            throw new Error(`Invalid zone string: ${input}`);
        }
        return new HeatMapZone(match[1], [
            { x: parseInt(match[2], 10), y: parseInt(match[3], 10) },
            { x: parseInt(match[4], 10), y: parseInt(match[5], 10) },
            { x: parseInt(match[6], 10), y: parseInt(match[7], 10) },
            { x: parseInt(match[8], 10), y: parseInt(match[9], 10) },
        ]);
    }

    constructor(name: string, points: ReadonlyArray<Readonly<Point>>) {
        this.name = name;
        this.points = points;
    }

    rename(name: string): HeatMapZone {
        // The v6 Web Client excluded double quotes and colons, so we'll do the same.
        name = name.replace('"', '').replace(':', '');
        return new HeatMapZone(name, this.points);
    }

    offset(dx: number, dy: number, xMax: number, yMax: number): HeatMapZone {
        dx = Math.round(dx);
        dy = Math.round(dy);

        // Choose which points to examine for clipping depending on which direction we're moving
        // (e.g. if moving left, only test the top-left and bottom-left points)
        const hPoints = dx < 0 ? [ this.points[0], this.points[2] ] : dx > 0 ? [ this.points[1], this.points[3] ] : [];
        const [ clippedDx = 0 ] = hPoints.map(({ x }) => {
            if (x + dx < 0) {
                return -x;
            }
            else if (x + dx > xMax) {
                return xMax - x;
            }
            return dx;
        }).sort((a, b) => (a - b) * Math.sign(dx));

        const vPoints = dy < 0 ? [ this.points[0], this.points[1] ] : dy > 0 ? [ this.points[2], this.points[3] ] : [];
        const [ clippedDy = 0 ] = vPoints.map(({ y }) => {
            if (y + dy < 0) {
                return -y;
            }
            else if (y + dy > yMax) {
                return yMax - y;
            }
            return dy;
        }).sort((a, b) => (a - b) * Math.sign(dy));

        return new HeatMapZone(this.name, this.points.map(pt => ({
            x: pt.x + clippedDx,
            y: pt.y + clippedDy,
        })));
    }

    movePoint(index: PointIndex, x: number, y: number, xMax: number, yMax: number): HeatMapZone {
        const newPoint = {
            x: Math.round(Math.max(0, Math.min(x, xMax))),
            y: Math.round(Math.max(0, Math.min(y, yMax))),
        };

        return new HeatMapZone(this.name, this.points.map((pt, i) =>
            i === index
                ? newPoint
                : pt
        ));
    }

    // Adjust the order of points so that we have no crossing lines
    reorder(): HeatMapZone {
        // Note: the array is [ topLeft, topRight, bottomLeft, bottomRight ], like a Z shape.
        const [ point1, point2, point3, point4 ] = this.points;

        // We have lines (p1, p2), (p1, p3), (p2, p4), (p3, p4).
        // Ensure that none of the lines are crossing eachother.
        // If they do cross, uncross by swapping two of the points.
        // Do the swap such that point1 remains the same (because
        // the zone label is anchored to point1 and we don't want
        // it to jump somewhere else)
        
        if (doLinesIntersect(point1, point2, point3, point4)) {
            // Swap points 2, 3, and 4 (keeps things 'clockwise')
            return new HeatMapZone(this.name, [ point1, point3, point4, point2 ]);
        }
        else if (doLinesIntersect(point1, point3, point2, point4)) {
            // Swap points 3 and 4
            return new HeatMapZone(this.name, [ point1, point2, point4, point3 ]);
        }
        else {
            // No crossing lines; nothing to do.
            return this;
        }
    }

    toString(): string {
        const { name, points } = this;
        return `${name}:${points[0].x},${points[0].y},${points[1].x},${points[1].y},${points[2].x},${points[2].y},${points[3].x},${points[3].y}`;
    }
}



// WEBPACK FOOTER //
// ./src/logic/reports/HeatMapZone.ts